import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AudioPlayer } from '@/components/data/AudioPlayer'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { usePermissions } from '@/hooks/api'
import { parseConversationMeta } from '@/lib/conversationChannel'
import { formatConversationDateTime } from '@/lib/dateFormat'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { ConversationAdmin } from '../components/ConversationAdmin'
import { formatDurationCompact } from '../components/DurationDisplay'
import { InboxChannelIcons } from './InboxChannelIcons'
import { getInboxChannelLabelKey } from './inboxChannel'
import { isTypingTarget } from './keyboard'
import { InboxNotes } from './InboxNotes'
import { InboxOutcomeBadge } from './InboxOutcomeBadge'
import type { Assistant } from '@/types/assistant'
import type { Conversation } from '@/types/conversation'

export type InboxTranscriptTab = 'phone' | 'whatsapp'
export type InboxDetailsTab = InboxTranscriptTab | 'overview' | 'admin'

interface InboxDetailsProps {
  conversation: Conversation
  assistant?: Assistant
  tab: InboxDetailsTab
  onTabChange: (tab: InboxDetailsTab) => void
  /** Transcripts shown as leading tabs; the mobile view has no separate thread column */
  transcriptTabs?: Partial<Record<InboxTranscriptTab, React.ReactNode>>
  /** Renders only the tabs, without the details header and card frame */
  embedded?: boolean
  className?: string
}

const tabClassName =
  'px-1 pt-0 pb-3 text-sm text-gray-500 selected:border-gray-900 selected:text-gray-900 dark:selected:border-gray-100 dark:selected:text-gray-100'

const detailCardClass =
  'rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900'

function InboxOverviewPanel({
  conversation,
  assistant,
}: {
  conversation: Conversation
  assistant?: Assistant
}) {
  return (
    <div className="flex flex-col gap-2">
      <InboxOverview conversation={conversation} assistant={assistant} />
      <InboxNotes conversationId={conversation.id} customerId={assistant?.customer_id} />
    </div>
  )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-4">
      <dt className="w-26 shrink-0 text-[13px] leading-4 text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="min-w-0 flex-1 text-[13px] leading-4 text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  )
}

function InboxOverview({ conversation, assistant }: { conversation: Conversation; assistant?: Assistant }) {
  const { t } = useTranslation()
  const token = useAuthStore((state) => state.accessToken)

  const recordingUrl = useMemo(() => {
    if (!token || !parseConversationMeta(conversation.meta)?.recording_url) return null
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    return `${apiUrl}/conversations/${conversation.id}/recording.wav?access_token=${token}`
  }, [conversation.meta, conversation.id, token])

  return (
    <div className={cn(detailCardClass, 'flex flex-col gap-4 p-4')}>
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        {t('resources.conversations.inbox.tabs.overview')}
      </h3>
      {recordingUrl && (
        <AudioPlayer src={recordingUrl} twilioId={conversation.twilio_id} variant="compact" />
      )}
      <dl className="flex flex-col gap-4">
        <DetailRow label={t('resources.conversations.inbox.fields.assistant')}>
          {assistant?.name ? (
            <Link to={`/assistants/${assistant.id}`} className="hover:underline">
              {assistant.name}
            </Link>
          ) : (
            t('resources.conversations.unknown')
          )}
        </DetailRow>
        <DetailRow label={t('resources.conversations.inbox.fields.name')}>
          {conversation.from_name || t('resources.conversations.unknown')}
        </DetailRow>
        <DetailRow label={t('resources.conversations.inbox.fields.type')}>
          <span className="flex items-center gap-2">
            <InboxChannelIcons conversation={conversation} />
            {t(getInboxChannelLabelKey(conversation))}
          </span>
        </DetailRow>
        <DetailRow label={t('resources.conversations.inbox.fields.phone_number')}>
          {conversation.from_id ? (
            <a href={`tel:${conversation.from_id}`} className="hover:underline">
              {conversation.from_id}
            </a>
          ) : (
            t('resources.conversations.unknown')
          )}
        </DetailRow>
        <DetailRow label={t('resources.conversations.inbox.fields.date')}>
          {formatConversationDateTime(conversation.created_at, assistant?.timezone)}
        </DetailRow>
        <DetailRow label={t('resources.conversations.inbox.fields.duration')}>
          {conversation.duration ? formatDurationCompact(conversation.duration) : '—'}
        </DetailRow>
        <DetailRow label={t('resources.conversations.inbox.fields.outcome')}>
          {conversation.outcome ? <InboxOutcomeBadge outcome={conversation.outcome} /> : '—'}
        </DetailRow>
      </dl>
    </div>
  )
}

export function InboxDetails({
  conversation,
  assistant,
  tab,
  onTabChange,
  transcriptTabs = {},
  embedded = false,
  className,
}: InboxDetailsProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()

  const canSeeAdmin = (can('access:admin-panel') || can('view:partners')) && Boolean(conversation.meta)
  const hasPhone = transcriptTabs.phone !== undefined
  const hasWhatsApp = transcriptTabs.whatsapp !== undefined

  const visibleTabs = useMemo(() => {
    const tabs: InboxDetailsTab[] = []
    if (hasPhone) tabs.push('phone')
    if (hasWhatsApp) tabs.push('whatsapp')
    tabs.push('overview')
    if (canSeeAdmin) tabs.push('admin')
    return tabs
  }, [hasPhone, hasWhatsApp, canSeeAdmin])

  const showTabBar = visibleTabs.length > 1

  const selectedTab = visibleTabs.includes(tab) ? tab : visibleTabs[0]

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '[' && event.key !== ']') return
      if (isTypingTarget(event.target)) return
      const current = visibleTabs.indexOf(selectedTab)
      const next = event.key === '[' ? Math.max(0, current - 1) : Math.min(visibleTabs.length - 1, current + 1)
      if (next !== current) onTabChange(visibleTabs[next])
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [visibleTabs, selectedTab, onTabChange])

  return (
    <section className={cn('flex min-h-0 min-w-0 flex-col', className)}>
      {!embedded && (
        <div className="flex h-15 shrink-0 items-center border-b border-gray-200 bg-white px-4 dark:border-gray-800 dark:bg-gray-900">
          <h2 className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('resources.conversations.inbox.details')}
          </h2>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        {showTabBar ? (
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={(key) => onTabChange(key as InboxDetailsTab)}
            className={cn('min-h-0 flex-1 overflow-hidden pt-4', embedded && 'bg-white dark:bg-gray-900')}
          >
            <TabsList className="shrink-0 gap-3 px-4">
              {hasPhone && (
                <TabsTrigger id="phone" className={tabClassName}>
                  {t('resources.conversations.inbox.tabs.phone')}
                </TabsTrigger>
              )}
              {hasWhatsApp && (
                <TabsTrigger id="whatsapp" className={tabClassName}>
                  {t('resources.conversations.inbox.tabs.whatsapp')}
                </TabsTrigger>
              )}
              <TabsTrigger id="overview" className={tabClassName}>
                {t('resources.conversations.inbox.tabs.overview')}
              </TabsTrigger>
              {canSeeAdmin && (
                <TabsTrigger id="admin" className={tabClassName}>
                  {t('resources.conversations.tabs.admin')}
                </TabsTrigger>
              )}
            </TabsList>
            {hasPhone && (
              <TabsContent id="phone" className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                {transcriptTabs.phone}
              </TabsContent>
            )}
            {hasWhatsApp && (
              <TabsContent id="whatsapp" className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                {transcriptTabs.whatsapp}
              </TabsContent>
            )}
            <TabsContent id="overview" className="min-h-0 flex-1 overflow-y-auto p-2">
              <InboxOverviewPanel conversation={conversation} assistant={assistant} />
            </TabsContent>
            {canSeeAdmin && (
              <TabsContent id="admin" className="min-h-0 flex-1 overflow-y-auto p-2">
                <div className={cn(detailCardClass, 'px-4 py-6')}>
                  <ConversationAdmin conversation={conversation} />
                </div>
              </TabsContent>
            )}
          </Tabs>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            <InboxOverviewPanel conversation={conversation} assistant={assistant} />
          </div>
        )}
      </div>
    </section>
  )
}
